/**
 * Captures a still from a video in the browser at upload time (SCRUM-58).
 *
 * The server does not decode video. Extracting a frame in Rust would mean an
 * ffmpeg dependency in the VM image that someone then has to own, so the
 * browser captures the still, the API stores it beside the original, and the
 * thumbnail route serves it.
 *
 * Every failure path resolves to null rather than rejecting. A video with no
 * poster falls back to a filetype placeholder, and a failed capture must never
 * fail the send.
 */

/** Matches `THUMBNAIL_MAX_EDGE` in `api/src/domains/chat_attachments.rs`. */
const POSTER_MAX_EDGE = 480;

/**
 * How far into the video to seek. Phone cameras commonly record a black or
 * half-exposed first frame, and a black poster is worse than a placeholder.
 */
const SEEK_SECONDS = 0.5;

/** A slow decode must not hold the upload open indefinitely. */
const CAPTURE_TIMEOUT_MS = 5000;

/** Low enough to stay small on mobile data, high enough for a 480px preview. */
const POSTER_QUALITY = 0.7;

/**
 * Scales to fit inside a square without distorting, and never scales up: a
 * 240 pixel video stays 240 rather than being blown up to 480.
 */
function posterSize(width: number, height: number) {
  const scale = Math.min(POSTER_MAX_EDGE / width, POSTER_MAX_EDGE / height, 1);
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

export function capturePosterFrame(file: File): Promise<Blob | null> {
  return new Promise((resolve) => {
    let objectUrl: string | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let settled = false;

    const video = document.createElement("video");

    const settle = (poster: Blob | null) => {
      if (settled) return;
      settled = true;
      if (timer !== null) clearTimeout(timer);
      // Detach before revoking so a late event cannot re-enter this.
      video.onloadedmetadata = null;
      video.onseeked = null;
      video.onerror = null;
      video.removeAttribute("src");
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      resolve(poster);
    };

    const draw = () => {
      try {
        const { videoWidth, videoHeight } = video;
        if (!videoWidth || !videoHeight) {
          settle(null);
          return;
        }
        const { width, height } = posterSize(videoWidth, videoHeight);
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;

        const context = canvas.getContext("2d");
        if (!context) {
          settle(null);
          return;
        }
        context.drawImage(video, 0, 0, width, height);

        if (typeof canvas.toBlob !== "function") {
          settle(null);
          return;
        }
        // A cross-origin or DRM-protected source taints the canvas and throws
        // here rather than at drawImage.
        canvas.toBlob(
          (blob) => settle(blob),
          "image/jpeg",
          POSTER_QUALITY,
        );
      } catch {
        settle(null);
      }
    };

    try {
      objectUrl = URL.createObjectURL(file);
    } catch {
      settle(null);
      return;
    }

    timer = setTimeout(() => settle(null), CAPTURE_TIMEOUT_MS);

    // Muted and inline so a mobile browser will decode without a user gesture.
    video.muted = true;
    video.playsInline = true;
    video.preload = "metadata";
    video.onerror = () => settle(null);

    video.onloadedmetadata = () => {
      const { duration } = video;
      // A stream of unknown length still has a decodable first frame.
      const target =
        Number.isFinite(duration) && duration > 0
          ? Math.min(SEEK_SECONDS, duration / 2)
          : 0;
      video.onseeked = draw;
      try {
        video.currentTime = target;
      } catch {
        settle(null);
      }
    };

    video.src = objectUrl;
  });
}
